<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'deletedRecordsHome' }"
          marginBottom="5"/>

      <h6 class="element-header"> Kunden</h6>


      <div class="element-box">
        <h5 class="form-header mb-4">
          Kundenliste gelöscht
        </h5>

        <div class="row align-items-center justify-content-between">
          <PerPage :values="[10, 25, 50, 100]" @update:perPage="getUnemployedList"/>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input name="searchTerm"
                   class="form-control"
                   @input="handleDebounceSearch"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!unemployedList.data?.length"
            icon="fa-trash-can"
            title="Keine gelöschten Einträge gefunden"
            subtitle="Es wurden keine gelöschten Einträge gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Kundennumer</th>
              <th>Status</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Standort</th>
              <th>Telefonnummer</th>
              <th>Erstellt am Tag</th>
              <th style=" text-align: right;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(unemployed) in unemployedList.data" :key="unemployed.id">
              <td>{{ unemployed?.customerNumber }}</td>
              <td>{{ unemployed?.statusTranslated }}</td>
              <td>{{ unemployed?.firstName }}</td>
              <td>{{ unemployed?.lastName }}</td>
              <td>{{ unemployed?.location?.name }}</td>
              <td>{{ unemployed?.phone }}</td>
              <td>{{ unemployed?.createdAt }}</td>
              <td class="text-right">

                <button class="btn btn-primary" v-if="unemployed.status === 'potential' || unemployed.status === 'waiting_list'"
                        @click="router.push({
                        name: 'unemployedPotentialForm',
                         params: { id: unemployed?.id }
                        })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-primary" v-else
                        @click="router.push({
                        name: 'unemployedPrivateCoachingList',
                         params: { id: unemployed?.id }
                        })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-primary ml-2"
                        @click="undeleteModal(unemployed?.id, unemployed.firstName, unemployed.lastName)">
                  <i class="os-icon os-icon-arrow-left7"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="unemployedList?.pagination?.total_pages > 1"
            :totalPages="unemployedList?.pagination?.total_pages"
            :currentPage="unemployedList?.pagination?.current_page"
            :totalItems="unemployedList?.pagination?.total_items"
            :perPage="unemployedList?.pagination?.items_per_page"
            @pagechanged="getUnemployedList"/>
      </div>
    </div>


    <ModalConfirm
        v-model="showModal"
        :confirmationQuestion=confirmModalMessage
        :header="''"
        :onConfirm="undeleteUnemployed"/>

  </PageTemplate>
</template>


<script setup>
import deletedRecordsService from '@/api/DeletedRecords';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import debounce from '@popperjs/core/lib/utils/debounce';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BackButton from "@/components/button/BackButton.vue";

const { t }               = useI18n();
const unemployedList      = ref([]);
const confirmModalMessage = ref('');
const showModal           = ref(false);
const unemployedId        = ref('');
const isSearching         = ref(false);
const isLoaded            = ref(false);

const filterDebounce = ref({
  searchTerm: '',
});

const debouncedSearch = debounce(async (query) => {
  await getUnemployedList(1, undefined, filterDebounce.value);
}, 600);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getUnemployedList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = unemployedList.value?.pagination?.items_per_page;

  try {
    const { data } = await deletedRecordsService.getDeletedUnemployedList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : ''),
    });

    unemployedList.value = data;
    isSearching.value    = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function undeleteModal(id, firstName, lastName) {
  CloseToast();
  unemployedId.value        = '';
  confirmModalMessage.value = '';
  unemployedId.value        = id;
  showModal.value           = true;
  confirmModalMessage.value = 'Willst du ' + firstName + ' ' + lastName + ' wiederherstellen?';
}

async function undeleteUnemployed() {
  try {
    await deletedRecordsService.undeleteUnemployed(unemployedId.value);

    unemployedId.value = false;
    showModal.value    = false;

    await getUnemployedList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getUnemployedList(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>

</style>